import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { createZodFormResolver, showSuccessAlert, lt, useFeatureFlag } from '@mews/framework';
import { SteppedDialogFormProps, useSteppedDialogForm, useSteppedDialogFormControls } from '@mews-commander/form';
import { RateGroupOptions } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { useCurrentServiceIdContext } from '../../../../contexts';
import { useRateGroupsAdd } from '../../../../mutations';
import {
    RateGroupFormValues,
    createRateGroupDetailsSchema,
    createRateGroupSchema,
    toRateGroupAddParameters,
} from '../../../../schemas/rateGroupSchema';
import { RateGroupFormStep } from '../../../../types';
import { RateGroupFormDetailFields, RateGroupFormPaymentPoliciesFields } from '../fields';

export const useRateGroupDialogForm = () =>
    useSteppedDialogForm<RateGroupFormStep>({
        id: 'rateGroupAddForm',
        title: lt('RateGroupCreate'),
        steps: ['details', 'paymentPolicies'],
        stepLabels: {
            details: lt('DetailPlural'),
            paymentPolicies: lt('GeneralPolicyPlural'),
        },
        renderForm: (props) => <RateGroupForm {...props} />,
    });

const RateGroupForm = (props: SteppedDialogFormProps<RateGroupFormStep>) => {
    const { defaultLanguageCode } = useCurrentEnterprise();
    const serviceId = useCurrentServiceIdContext();

    const { mutateAsync } = useRateGroupsAdd();

    const rateGroupDetailSchema = createRateGroupDetailsSchema({ defaultLanguageCode });
    const rateGroupSchema = createRateGroupSchema({ defaultLanguageCode });
    const requirePaymentDuringOCI = useFeatureFlag(FeatureFlag.requirePaymentDuringOnlineCheckIn);

    const { renderFormContent } = useSteppedDialogFormControls<RateGroupFormStep, RateGroupFormValues>({
        ...props,
        stepResolvers: {
            details: createZodFormResolver(rateGroupDetailSchema),
            paymentPolicies: createZodFormResolver(rateGroupSchema),
        },
        defaultValues: {
            ordering: 0,
        },
        onSubmit: async (formValues) => {
            const { requirePaymentCardDuringOnlineCheckIn, ...restFormValues } = formValues;

            const defaultOptions = restFormValues.options ? restFormValues.options : [];
            const mixOptions = requirePaymentCardDuringOnlineCheckIn
                ? [...defaultOptions, RateGroupOptions.RequirePaymentCardDuringOnlineCheckIn]
                : defaultOptions;

            await mutateAsync(
                {
                    serviceId,
                    rateGroups: [
                        rateGroupSchema.transform(toRateGroupAddParameters({ serviceId })).parse({
                            ...restFormValues,
                            ...{
                                //  This is momentary while the feature is enable for all users. This will allow us to activate users ad they will have by default the option selected.
                                options: !requirePaymentDuringOCI
                                    ? [...defaultOptions, RateGroupOptions.RequirePaymentCardDuringOnlineCheckIn]
                                    : mixOptions,
                            },
                        }),
                    ],
                },
                {
                    onSuccess: () => {
                        showSuccessAlert({
                            title: lt('RateGroupAddSuccessMessageTitle'),
                            description: lt('RateGroupAddSuccessMessageDescription', {
                                rateGroupName: formValues.names[defaultLanguageCode],
                            }),
                        });
                    },
                }
            );
        },
    });

    return renderFormContent((step) => {
        switch (step) {
            case 'details':
                return <RateGroupFormDetailFields />;
            case 'paymentPolicies':
                return <RateGroupFormPaymentPoliciesFields />;
        }
    });
};
